import { useTranslation } from "react-i18next";
import { StyleSheet } from "react-native";
import { List, Text } from "react-native-paper";
import { useAppTheme } from "../../useAppTheme";
import Box from "../Box";
import { LeaderboardAvatar } from "./LeaderboardAvatar";
import { LeaderboardXpBadge } from "./LeaderboardXpBadge";
import type { LeaderboardAvatarType, LeaderboardItem } from "./types";

export type LeaderboardListItemProps = LeaderboardItem & {
	isCurrentUser?: boolean;
	avatarType?: LeaderboardAvatarType;
};

export const LeaderboardListItem = ({
	rank,
	xp,
	avatar,
	avatarType,
	username,
	isCurrentUser = false,
}: LeaderboardListItemProps) => {
	const theme = useAppTheme();
	const { t } = useTranslation();

	const isFirst = rank === 1;

	return (
		<List.Item
			title={
				<Box flexDirection="row" alignItems="center" gap={8} overflow="hidden">
					<Text
						variant="bodyMedium"
						numberOfLines={1}
						style={[styles.username, { color: theme.colors.onSurface }]}
					>
						{username}
					</Text>
					{isCurrentUser && (
						<Box
							borderRadius={99}
							paddingHorizontal={6}
							paddingVertical={3}
							backgroundColor={theme.colors.primary}
						>
							<Text
								style={[styles.youChipText, { color: theme.colors.onPrimary }]}
							>
								{t("leaderboard.youBadge")}
							</Text>
						</Box>
					)}
				</Box>
			}
			left={() => (
				<Box flexDirection="row" alignItems="center" gap={12}>
					<Text
						variant="titleMedium"
						style={[
							styles.rank,
							{
								color: isFirst
									? theme.colors.primary
									: theme.colors.onSurfaceVariant,
							},
						]}
					>
						{rank}
					</Text>
					<Box borderRadius={999} overflow="hidden">
						<LeaderboardAvatar
							username={username}
							size={36}
							avatarImage={avatar}
							avatarType={avatarType}
						/>
					</Box>
				</Box>
			)}
			right={() => (
				<Box justifyContent="center">
					<LeaderboardXpBadge
						value={xp}
						highlighted={isFirst || isCurrentUser}
					/>
				</Box>
			)}
			style={[
				styles.container,
				isCurrentUser && {
					backgroundColor: theme.colors.secondaryContainer,
					borderColor: theme.colors.primary,
				},
			]}
		/>
	);
};

const styles = StyleSheet.create({
	container: {
		borderLeftWidth: 3,
		borderColor: "transparent",
		paddingHorizontal: 16,
		paddingVertical: 4,
	},
	rank: {
		width: 28,
		textAlign: "right",
	},
	username: {
		flexShrink: 1,
	},
	youChipText: {
		fontSize: 10,
		fontWeight: "700",
		letterSpacing: 0.5,
	},
});
